import { useBell } from '@magicbell/react-headless';
import * as React from 'react';
import type { TouchableOpacity } from 'react-native';

import { webConfig } from '@gitbook/env';
import {
    Popover,
    PopoverPortalWrapper,
    PortalToggleable,
    type PortalToggleableRef,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { SidebarButton } from '@gitbook/spine-react';

import { NotificationsPanel } from './NotificationsPanel';
/**
 * Button to open the notification panel.
 */
export const NotificationsBell = process.env.GITBOOK_PREDICTABLE_UI
    ? PredictableNotificationsBell
    : webConfig.magicbell.apiKey
      ? MagicBellNotificationsBell
      : NoopNotificationsBell;

/**
 * An implementation of the notification bell used for predictable UIs.
 */
function PredictableNotificationsBell(): React.ReactElement {
    return <NotificationBell opened={false} hasUnread />;
}

/**
 * An implementation of the notification bell that does nothing.
 */
function NoopNotificationsBell(): React.ReactElement {
    return <NotificationBell opened={false} />;
}

/**
 * An implementation of the notification bell, backed by MagicBell
 */
function MagicBellNotificationsBell() {
    const notifications = useBell({ storeId: 'unread' });
    const hasUnread = notifications ? notifications.unreadCount > 0 : false;
    const portalRef = React.useRef<PortalToggleableRef>(null);

    return (
        <PortalToggleable
            ref={portalRef}
            wrapper={PopoverPortalWrapper}
            anchor={({ ref, toggle, opened }) => (
                <NotificationBell
                    buttonRef={ref}
                    opened={opened}
                    onPress={() => {
                        notifications?.markAllAsSeen();
                        toggle();
                    }}
                    hasUnread={hasUnread}
                />
            )}
        >
            <Popover kind="wide">
                <NotificationsPanel />
            </Popover>
        </PortalToggleable>
    );
}

function NotificationBell(props: {
    buttonRef?: React.Ref<TouchableOpacity>;
    opened: boolean;
    onPress?: () => void;
    hasUnread?: boolean;
}): React.ReactElement {
    const { buttonRef, hasUnread, opened, onPress } = props;

    return (
        <SidebarButton
            ref={buttonRef as React.Ref<HTMLButtonElement>}
            data-testid="sidebar.notificationsButton"
            icon={hasUnread ? Icon.BellUnread : Icon.Bell}
            iconOnly
            label="Notifications"
            aria-expanded={opened}
            onPress={onPress}
        />
    );
}
